import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { InView } from 'react-intersection-observer';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { Advertisement } from '@interfaces/homepage';
import { ClickTarget, ContentType, ImpressionElement, click, impression } from '@tracking';
import { hasUserOptedInToTrackingConsent } from '@utils/consent';

interface HorizontalAdvertisementProps {
  advertisement: Advertisement;
  trackingLocation: string;
}

const HorizontalAdvertisement = ({
  advertisement,
  trackingLocation,
}: HorizontalAdvertisementProps) => {
  const hydrated = useServerSideRendering();
  const { Track, trackEvent } = useEventTracking({
    contentType: ContentType.Advertisement,
    contentId: advertisement.id,
    linkedAdScheduleId: advertisement.adScheduleId,
    location: trackingLocation,
  });

  return (
    <Track>
      <InView
        triggerOnce={true}
        onChange={(inView) => {
          inView &&
            trackEvent(impression({ impressionElement: ImpressionElement.FeaturedPartnerAd }));
        }}
      >
        <aside
          className="border-0 border-b border-solid border-grayscale-gray px-16 py-48 sm:px-0"
          data-testid="featured-partner-horizontal"
        >
          <div className="w-full bg-grayscale-beige p-12">
            <div className="flex flex-col items-center justify-between gap-12 border-2 border-solid border-white p-16 sm:flex-row sm:gap-[50px] sm:px-40 sm:py-24">
              <div>
                <h3 className="mb-4 mt-0 font-suisse-intl-mono text-body-sm font-normal text-grayscale-gray">
                  Featured Partner
                </h3>
                <h2 className="mb-4 mt-0 font-suisse-screen text-headline-alt-sm font-medium text-primary-black">
                  {advertisement.sponsorName}
                </h2>
                <p className="mb-24 mt-0 font-suisse-intl text-description-sm text-grayscale-charcoal">
                  {ReactHtmlParser(advertisement.text)}
                </p>
                <p className="m-0">
                  <a
                    className="font-suisse-intl text-description-sm text-primary-black underline  hover:text-primary-magenta dark:hover:text-primary-pink"
                    href={advertisement.clickthroughUrl}
                    onClickCapture={() =>
                      trackEvent(click({ clickTarget: ClickTarget.FeaturedPartnerAd }))
                    }
                    target="_blank"
                    rel="noreferrer sponsored"
                  >
                    Learn more
                  </a>
                </p>
              </div>
              <div className="hidden self-stretch border-0 border-l-2 border-solid border-l-white sm:block"></div>
              <div className="flex items-center justify-center self-end sm:self-auto">
                <LazyLoadImage
                  src={advertisement.sponsorLogo}
                  alt={`${advertisement.sponsorName} logo`}
                  height={40}
                  className="h-32 sm:h-40"
                />
              </div>
            </div>
          </div>
          {hydrated && advertisement.trackingPixelUrl && hasUserOptedInToTrackingConsent() && (
            <img src={advertisement.trackingPixelUrl} className="hidden" />
          )}
        </aside>
      </InView>
    </Track>
  );
};

export default HorizontalAdvertisement;
